<template>
  <page class="data-overview-page">
    <div class="toolbar">
      <div class="toolbar-left">
        <DateTimeRangePicker v-model="dateRange" picker-type="daterange" size="mini" :clearable="false" style="width: 300px" />
        <div class="quick">
          <el-button
            size="mini"
            v-for="item in quickRangeOptions"
            :key="item.key"
            :type="currentDateRangeKey === item.key ? 'primary' : 'default'"
            @click="applyQuickRange(item.key)"
            >{{ item.label }}</el-button
          >
        </div>
      </div>

      <!-- <div class="toolbar-right">
        <div class="toolbar-right-item" v-for="item in platformOptions" :key="item.key">
          <span class="value">{{ item.value }}</span>
          <span>{{ item.label }}</span>
        </div>
      </div> -->
    </div>

    <div class="cards" v-loading="loading">
      <el-row :gutter="16">
        <el-col v-for="card in cards" :key="card.key" :span="6">
          <el-card shadow="never" class="stat-card">
            <div class="stat-card-inner" :class="{ 'is-compact': !(card.sub && card.sub.length) }">
              <div class="stat-title">{{ card.title }}</div>
              <div class="stat-value" :class="{ 'is-large': !(card.sub && card.sub.length) }" v-if="card.showValue !== false">
                {{ formatValue(card.value, card.format) }}
              </div>
              <div v-if="card.sub && card.sub.length" class="stat-sub">
                <div v-for="item in card.sub" :key="item.label" class="stat-sub-row">
                  <div class="stat-sub-label">{{ item.label }}</div>
                  <div class="stat-sub-value">{{ formatValue(item.value, item.format) }}</div>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </page>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { getDataStatisticsHome } from '@/api/statistics/dataStatistics'
import { toUnixSeconds } from '@/utils/time'
import DateTimeRangePicker from '@/components/DateTimeRangePicker'
import { DISPLAY_TIMEZONE, getCalendarPartsInTimeZone } from '@/utils/displayTimezone'
import config from '@/config'
import { centsToYuan } from '@/utils/amount'
const quickRangeOptions = [
  { key: 'today', label: '今日' },
  { key: 'yesterday', label: '昨日' },
  { key: 'thisWeek', label: '本周' },
  { key: 'lastWeek', label: '上周' },
  { key: 'thisMonth', label: '本月' },
]

/** 接口暂无在线人数等字段，统一占位 */
const platformOptions = computed(() => [
  { key: 'online', label: '当前在线', value: '-' },
  { key: 'android', label: 'Android', value: '-' },
  { key: 'ios', label: 'iOS', value: '-' },
  { key: 'h5', label: '移动端', value: '-' },
  { key: 'web', label: 'Web', value: '-' },
])

function pad2(n) {
  return n < 10 ? `0${n}` : String(n)
}

function formatYMD({ y, m, d }) {
  return `${y}-${pad2(m)}-${pad2(d)}`
}

function addDaysYMD({ y, m, d, a }, deltaDays) {
  const base = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)))
  base.setUTCDate(base.getUTCDate() + deltaDays)
  const next = { y: base.getUTCFullYear(), m: base.getUTCMonth() + 1, d: base.getUTCDate() }
  if (a != null) {
    next.a = (Number(a) + deltaDays) % 7
    if (next.a < 0) next.a += 7
  }
  return next
}

function lastDayOfMonthYMD({ y, m }) {
  const d = new Date(Date.UTC(Number(y), Number(m), 0))
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() }
}

function getLagosTodayYMD() {
  const now = new Date()
  const p = getCalendarPartsInTimeZone(now, DISPLAY_TIMEZONE)
  return { y: p.y, m: p.m, d: p.d, a: p.a }
}

const dateRange = ref([formatYMD(getLagosTodayYMD()), formatYMD(getLagosTodayYMD())])
const currentDateRangeKey = ref('today')
const loading = ref(false)

function emptyOverview() {
  return {
    totalProfit: null,
    launchRecharge: { amount: null, users: null, times: null },
    inboundRecharge: { amount: null, users: null, times: null, rate: null, arppu: null },
    launchWithdraw: { amount: null, users: null, times: null },
    inboundWithdraw: { amount: null, users: null, times: null },
    loginUsers: { count: null, betUsers: null, betRate: null, activeUsers: null },
    newUsers: {
      count: null,
      registerDepositUsers: null,
      registerBetUsers: null,
      registerDepositAmount: null,
      registerDepositRate: null,
      registerDepositPerCapita: null,
    },
    winLoseDiff: { amount: null, betAmount: null },
    agentWithdraw: { commissionAmount: null, withdrawAmount: null },
  }
}

const overview = ref(emptyOverview())

/** 接口字段 handler_admin_data_statistics.DataStatisticsEle，金额单位：分 */
function mapApiToOverview(raw) {
  return {
    totalProfit: raw.profit_amount,
    launchRecharge: {
      amount: raw.payin_amount,
      users: raw.payin_unique_num,
      times: raw.payin_count,
    },
    inboundRecharge: {
      amount: raw.success_payin_amount,
      users: raw.success_payin_unique_num,
      times: raw.success_payin_count,
      rate: raw.payin_rate,
      arppu: raw.arrpu,
    },
    launchWithdraw: {
      amount: raw.payout_amount,
      users: raw.payout_unique_num,
      times: raw.payout_count,
    },
    inboundWithdraw: {
      amount: raw.success_payout_amount,
      users: raw.success_payout_unique_num,
      times: raw.success_payout_count,
    },
    loginUsers: {
      count: raw.login_unique_num,
      betUsers: raw.login_bet_unique_num,
      betRate: raw.login_bet_rate,
      activeUsers: raw.login_active_unique_num,
    },
    newUsers: {
      count: raw.register_unique_num,
      registerDepositUsers: raw.register_success_payin,
      registerBetUsers: raw.register_bet,
      registerDepositAmount: raw.register_success_payin_amount,
      registerDepositRate: raw.register_success_payin_rate,
      registerDepositPerCapita: raw.register_success_payin_amount_average,
    },
    winLoseDiff: {
      amount: raw.win_loss_amount,
      betAmount: raw.bet_amount,
    },
    agentWithdraw: {
      commissionAmount: raw.agent_back_gold,
      withdrawAmount: raw.agent_to_gold,
    },
  }
}

function buildTimeParams() {
  const r = dateRange.value
  if (!r || r.length < 2 || !r[0] || !r[1]) {
    return { start_at: 0, end_at: 0 }
  }
  const start = toUnixSeconds(`${r[0]} 00:00:00`)
  const end = toUnixSeconds(`${r[1]} 23:59:59`)
  return {
    start_at: start || 0,
    end_at: end || 0,
  }
}

const loadOverview = async () => {
  loading.value = true
  try {
    const res = await getDataStatisticsHome(buildTimeParams())
    overview.value = mapApiToOverview(res.data.data_statistics || {})
  } catch (e) {
    overview.value = emptyOverview()
  } finally {
    loading.value = false
  }
}

const cards = computed(() => {
  const d = overview.value
  return [
    { key: 'totalProfit', title: `总盈利 (${config.currency})`, value: d.totalProfit, format: 'money' },
    {
      key: 'launchRecharge',
      title: `发起充值 (${config.currency})`,
      value: d.launchRecharge.amount,
      format: 'money',
      sub: [
        { label: '人数', value: d.launchRecharge.users, format: 'int' },
        { label: '笔数', value: d.launchRecharge.times, format: 'int' },
      ],
    },
    {
      key: 'inboundRecharge',
      title: `入账充值 (${config.currency})`,
      value: d.inboundRecharge.amount,
      format: 'money',
      sub: [
        { label: '人数', value: d.inboundRecharge.users, format: 'int' },
        { label: '笔数', value: d.inboundRecharge.times, format: 'int' },
        { label: '充值成功率', value: d.inboundRecharge.rate, format: 'percentStr' },
        { label: 'ARPPU', value: d.inboundRecharge.arppu, format: 'money' },
      ],
    },
    {
      key: 'launchWithdraw',
      title: `发起提现 (${config.currency})`,
      value: d.launchWithdraw.amount,
      format: 'money',
      sub: [
        { label: '人数', value: d.launchWithdraw.users, format: 'int' },
        { label: '笔数', value: d.launchWithdraw.times, format: 'int' },
      ],
    },
    {
      key: 'inboundWithdraw',
      title: `入账提现 (${config.currency})`,
      value: d.inboundWithdraw.amount,
      format: 'money',
      sub: [
        { label: '人数', value: d.inboundWithdraw.users, format: 'int' },
        { label: '笔数', value: d.inboundWithdraw.times, format: 'int' },
      ],
    },
    {
      key: 'loginUsers',
      title: '登录用户 (人)',
      value: d.loginUsers.count,
      format: 'int',
      sub: [
        { label: '投注用户', value: d.loginUsers.betUsers, format: 'int' },
        { label: '投注占比', value: d.loginUsers.betRate, format: 'percentStr' },
        { label: '活跃用户', value: d.loginUsers.activeUsers, format: 'int' },
      ],
    },
    {
      key: 'newUsers',
      title: '新增用户 (人)',
      value: d.newUsers.count,
      format: 'int',
      sub: [
        { label: '新增且充值用户', value: d.newUsers.registerDepositUsers, format: 'int' },
        { label: '新增且投注用户', value: d.newUsers.registerBetUsers, format: 'int' },
        { label: '新增用户充值金额', value: d.newUsers.registerDepositAmount, format: 'money' },
        { label: '新增充值转化率', value: d.newUsers.registerDepositRate, format: 'percentStr' },
        { label: '新增人均充值', value: d.newUsers.registerDepositPerCapita, format: 'money' },
      ],
    },
    {
      key: 'winLoseDiff',
      title: `输赢差 (${config.currency})`,
      value: d.winLoseDiff.amount,
      format: 'money',
      sub: [{ label: '下注', value: d.winLoseDiff.betAmount, format: 'money' }],
    },
    {
      key: 'agentWithdraw',
      title: '代理提现',
      value: null,
      format: 'money',
      showValue: false,
      sub: [
        { label: '佣金总额', value: d.agentWithdraw.commissionAmount, format: 'money' },
        { label: '代理领取', value: d.agentWithdraw.withdrawAmount, format: 'money' },
      ],
    },
  ]
})

function applyQuickRange(key) {
  currentDateRangeKey.value = key
  if (key === 'today') {
    const t = getLagosTodayYMD()
    const ymd = formatYMD(t)
    dateRange.value = [ymd, ymd]
    return
  }
  if (key === 'yesterday') {
    const t = getLagosTodayYMD()
    const y = addDaysYMD(t, -1)
    const ymd = formatYMD(y)
    dateRange.value = [ymd, ymd]
    return
  }
  if (key === 'thisWeek') {
    const t = getLagosTodayYMD()
    const diffToMonday = t.a === 0 ? 6 : t.a - 1
    const monday = addDaysYMD(t, -diffToMonday)
    const sunday = addDaysYMD(monday, 6)
    dateRange.value = [formatYMD(monday), formatYMD(sunday)]
    return
  }
  if (key === 'lastWeek') {
    const t = getLagosTodayYMD()
    const diffToMonday = t.a === 0 ? 6 : t.a - 1
    const thisMonday = addDaysYMD(t, -diffToMonday)
    const lastMonday = addDaysYMD(thisMonday, -7)
    const lastSunday = addDaysYMD(lastMonday, 6)
    dateRange.value = [formatYMD(lastMonday), formatYMD(lastSunday)]
    return
  }
  if (key === 'thisMonth') {
    const t = getLagosTodayYMD()
    const first = { y: t.y, m: t.m, d: 1 }
    const last = lastDayOfMonthYMD({ y: t.y, m: t.m })
    dateRange.value = [formatYMD(first), formatYMD(last)]
  }
}

/** 仅 null / undefined / 空白字符串视为无值；0 与 '0' 为有效，展示 0 */
function isEmptyVal(val) {
  if (val === null || val === undefined) return true
  if (typeof val === 'string' && val.trim() === '') return true
  return false
}

/** 金额：接口为分，展示为config.currency */
function formatValue(val, fmt) {
  if (fmt === 'plain') {
    if (isEmptyVal(val)) return '-'
    return String(val)
  }
  if (isEmptyVal(val)) return '-'
  if (fmt === 'money') return centsToYuan(Number(val))
  if (fmt === 'arppu') {
    const n = Number(val)
    return Number.isFinite(n) ? n.toFixed(2) : '-'
  }
  if (fmt === 'percentStr') {
    if (typeof val === 'string') {
      if (isEmptyVal(val)) return '-'
      return val.includes('%') ? val : `${val}%`
    }
    const n = Number(val)
    return Number.isFinite(n) ? `${n.toFixed(1)}%` : '-'
  }
  if (fmt === 'int') {
    const n = Number(val)
    return Number.isFinite(n) ? `${n}` : '-'
  }
  const n = Number(val)
  return Number.isFinite(n) ? `${n}` : '-'
}

watch(dateRange, () => loadOverview(), { immediate: true, deep: true })
</script>

<style lang="scss" scoped>
.data-overview-page {
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .quick {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    .toolbar-right-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      flex-wrap: wrap;
      font-size: 12px;
      color: #606266;
      .value {
        font-size: 14px;
        font-weight: 700;
        color: #303133;
      }
    }
  }

  .cards {
    .stat-card {
      height: 100%;
      border-radius: 2px;
      background: #fff;
      height: 180px;
    }

    ::v-deep .el-col {
      margin-bottom: 16px;
    }

    ::v-deep .el-card__body {
      height: 100%;
      padding: 14px 14px 12px;
    }

    .stat-card-inner {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .stat-card-inner.is-compact {
      justify-content: center;
      align-items: center;
      text-align: center;
    }

    .stat-title {
      font-size: 13px;
      color: #303133;
      font-weight: 600;
      margin-bottom: 8px;
    }

    .stat-value {
      font-size: 24px;
      font-weight: 700;
      color: #303133;
      margin-bottom: 8px;
      line-height: 1.2;
      font-variant-numeric: tabular-nums;
      letter-spacing: 0.2px;
    }

    .stat-value.is-large {
      font-size: 34px;
      margin-bottom: 0;
    }

    .stat-sub {
      margin-top: 4px;
      color: #606266;
      font-size: 12px;
    }

    .stat-sub-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      line-height: 1.6;
    }

    .stat-sub-label {
      white-space: nowrap;
      opacity: 0.9;
    }

    .stat-sub-value {
      text-align: right;
      color: #303133;
      font-variant-numeric: tabular-nums;
    }
  }
}
</style>
